import React, { useEffect, useState } from "react";
import axios from "axios";

export default function CardSettings() {
  const [userData, setUserData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    role: "",
    admission_no: "",
    club_name: "",
    club_id: "",
  });

  const [clubs, setClubs] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch user data
  useEffect(() => {
    const userId = localStorage.getItem("user_id");
    const token = localStorage.getItem("token");

    if (!userId || !token) return;

    axios
      .get(`https://api.nvcti.in/user/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        if (response.data.success) {
          const user = response.data.user;
          const userDataObj = {
            first_name: user.first_name || "",
            last_name: user.last_name || "",
            email: user.email || "",
            phone: user.phone || "",
            role: user.role || "",
            admission_no: user.admission_no || "",
            // ensure consistency with clubs API naming
            club_name: user.club_name || "",
            club_id: user.club_id ? Number(user.club_id) : null,
            club_status: user.club_status || "pending",
          };
          setUserData(userDataObj);
          setOriginalData(userDataObj);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch user data:", error);
      });
  }, []);

  // Fetch clubs data
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      console.error("No token found for clubs API");
      return;
    }

    console.log("Fetching clubs with token:", token.substring(0, 20) + "...");

    axios
      .get("https://api.nvcti.in/clubs/getAllClubs", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        console.log("Clubs API response:", response.data);
        if (response.data.success) {
          const clubsArray = response.data.data || [];
          setClubs(clubsArray);
          console.log("Clubs set:", clubsArray);
        } else {
          console.log("No clubs found in response:", response.data.message);
          setClubs([]); // Set empty array when no clubs found
        }
      })
      .catch((error) => {
        console.error("Failed to fetch clubs:", error);
        if (error.response) {
          console.error("Error response:", error.response.data);
          console.error("Error status:", error.response.status);
        }
      });
  }, []);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setUserData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle club selection (API returns club_id and club_name)
  const handleClubChange = (clubId) => {
    // coerce to number when possible to match backend ids
    const parsedId = clubId === "" || clubId === null ? null : Number(clubId);
    const selectedClub = clubs.find((club) => club.club_id === parsedId);
    setUserData((prev) => ({
      ...prev,
      club_id: parsedId,
      club_name: selectedClub ? selectedClub.club_name : "",
    }));
  };

  // Save changes
  const handleSave = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");

    if (!token) {
      alert("No authentication token found. Please log in again.");
      setLoading(false);
      return;
    }

    try {
      // Require club selection for students
      if (userData.role === "student" && (userData.club_id === null || userData.club_id === undefined || userData.club_id === "")) {
        alert("Please select a club before saving your profile.");
        setLoading(false);
        return;
      }

      const updateData = {
        first_name: userData.first_name,
        last_name: userData.last_name,
        phone: userData.phone,
      };

      // Only include club_id when a valid selection exists to avoid sending null
      if (userData.club_id !== null && userData.club_id !== undefined && userData.club_id !== "") {
        updateData.club_id = userData.club_id;
      }

      console.log("Updating profile with data:", updateData);
      console.log("Using token:", token.substring(0, 20) + "...");

      const response = await axios.put("https://api.nvcti.in/user/updateProfile", updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      console.log("Update profile response:", response.data);

      if (response.data.success) {
        setOriginalData(userData);
        setIsEditing(false);
        alert("Profile updated successfully!");
      } else {
        alert("Failed to update profile: " + (response.data.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      if (error.response) {
        console.error("Error response:", error.response.data);
        console.error("Error status:", error.response.status);

        if (error.response.status === 401) {
          alert("Unauthorized. Please check your login status or contact support.");
        } else if (error.response.status === 400) {
          alert("Bad request. Please check your input data.");
        } else {
          alert(`Error updating profile: ${error.response.data.message || "Server error"}`);
        }
      } else {
        alert("Network error. Please check your connection and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Cancel editing
  const handleCancel = () => {
    setUserData(originalData);
    setIsEditing(false);
  };

  // Start editing
  const handleEdit = () => {
    setIsEditing(true);
  };

  return (
    <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-white/5 backdrop-blur-2xl border border-white/20 mt-10 text-white">
      <div className="rounded-t mb-0 px-6 py-6 border-b border-white/20">
        <div className="text-center flex justify-between">
          <h6 className="text-white text-xl font-bold">My Profile</h6>
          <div>
            {!isEditing ? (
              <button
                onClick={handleEdit}
                className="bg-lightBlue-600 text-white active:bg-lightBlue-700 text-xs font-bold uppercase px-4 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 border border-white/20 hover:bg-lightBlue-500"
                type="button"
              >
                Edit Profile
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="bg-emerald-500 text-white active:bg-emerald-600 text-xs font-bold uppercase px-4 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:opacity-50"
                  type="button"
                >
                  {loading ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-red-500 text-white active:bg-red-600 text-xs font-bold uppercase px-4 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  type="button"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex-auto px-4 lg:px-10 py-10 pt-0">
        <form>
          <h6 className="text-blueGray-400 text-sm mt-3 mb-6 font-bold uppercase">
            User Information
          </h6>
          <div className="flex flex-wrap">
            <div className="w-full lg:w-6/12 px-4">
              <div className="relative w-full mb-3">
                <label className="block uppercase text-blueGray-300 text-xs font-bold mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  className="border-0 px-3 py-3 placeholder-blueGray-400 text-white bg-white/10 rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150 border border-white/20"
                  value={userData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  disabled={!isEditing}
                />
              </div>
            </div>
            <div className="w-full lg:w-6/12 px-4">
              <div className="relative w-full mb-3">
                <label className="block uppercase text-blueGray-300 text-xs font-bold mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  className="border-0 px-3 py-3 placeholder-blueGray-400 text-white bg-white/10 rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150 border border-white/20"
                  value={userData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  disabled={!isEditing}
                />
              </div>
            </div>
            <div className="w-full lg:w-6/12 px-4">
              <div className="relative w-full mb-3">
                <label className="block uppercase text-blueGray-300 text-xs font-bold mb-2">
                  Email Address
                </label>
                <input
                  type="text"
                  className="border-0 px-3 py-3 placeholder-blueGray-400 text-white bg-white/10 rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150 opacity-70 border border-white/20"
                  value={userData.email}
                  disabled
                />
              </div>
            </div>
            <div className="w-full lg:w-6/12 px-4">
              <div className="relative w-full mb-3">
                <label className="block uppercase text-blueGray-300 text-xs font-bold mb-2">
                  Phone Number
                </label>
                <input
                  type="text"
                  className="border-0 px-3 py-3 placeholder-blueGray-400 text-white bg-white/10 rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150 border border-white/20"
                  value={userData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  disabled={!isEditing}
                />
              </div>
            </div>


            {/* Conditional rendering for Admission Number */}
            {userData.role === "student" && (
              <>
                <div className="w-full lg:w-6/12 px-4">
                  <div className="relative w-full mb-3">
                    <label className="block uppercase text-blueGray-300 text-xs font-bold mb-2">
                      Admission Number
                    </label>
                    <input
                      type="text"
                      className="border-0 px-3 py-3 placeholder-blueGray-400 text-white bg-white/10 rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150 opacity-70 border border-white/20"
                      value={userData.admission_no}
                      disabled
                    />
                  </div>
                </div>
                <div className="w-full lg:w-6/12 px-4">
                  <div className="relative w-full mb-3">
                    <label className="block uppercase text-blueGray-300 text-xs font-bold mb-2">
                      Club
                    </label>
                    {isEditing ? (
                      <div>
                        <select
                          className="border-0 px-3 py-3 placeholder-blueGray-400 text-white bg-white/10 rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150 border border-white/20"
                          value={userData.club_id ?? ""}
                          onChange={(e) => handleClubChange(e.target.value)}
                          required={userData.role === "student"}
                          disabled={userData.club_status === "approved"}
                        >
                          <option value="" disabled className="text-gray-500 bg-[#0f172a]">Select a club</option>
                          {clubs.length > 0 ? (
                            clubs.map((club) => (
                              <option key={club.club_id} value={club.club_id} className="text-white bg-[#0f172a]">
                                {club.club_name}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled className="text-gray-500 bg-[#0f172a]">Loading clubs...</option>
                          )}
                        </select>
                        {userData.club_status === "approved" && <p className="text-xs text-green-500 mt-1">Club approved. Contact admin to change.</p>}
                        {clubs.length === 0 && (
                          <p className="text-xs text-blueGray-500 mt-1">
                            No clubs are currently available.
                          </p>
                        )}
                      </div>
                    ) : (
                      <input
                        type="text"
                        className="border-0 px-3 py-3 placeholder-blueGray-400 text-white bg-white/10 rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150 opacity-70 border border-white/20"
                        value={userData.club_name}
                        disabled
                      />
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="w-full lg:w-6/12 px-4">
              <div className="relative w-full mb-3">
                <label className="block uppercase text-blueGray-300 text-xs font-bold mb-2">
                  Role
                </label>
                <input
                  type="text"
                  className="border-0 px-3 py-3 placeholder-blueGray-400 text-white bg-white/10 rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150 opacity-70 border border-white/20"
                  value={userData.role}
                  disabled
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
